import type {
  TrainingGpuQuotaRequestStatus,
  TrainingGpuQuotaScheduleEntry,
  TrainingGpuUsageShare,
} from "@/api";
import { formatGpuHours } from "@/components/gpu/usage/gpuUsageFormat";
import { isHttpClientError } from "@/http";
import { formatNumber } from "@/utils/formats";

// 쿼터 표의 순수 표시 헬퍼. 표와 행 모델은 이 함수들만 호출한다.

const emptyValue = "—";

function shortId(id: string): string {
  return `${id.slice(0, 8)}…`;
}

// 별칭이 없을 수 있는 표시명의 폴백. registry 에 별칭이 없으면 사용량 응답의 display_name 과
// 요청 레코드의 workspace_display_name 은 null 이다. 그때 UUID 앞자리를 표시해 행을 식별할 수
// 있게 한다. 쿼터 내역 행의 name 은 upstream 이 항상 보내므로 이 폴백을 쓰지 않는다.
export function resolveWorkspaceDisplayName(
  displayName: string | null,
  workspaceId: string,
): string {
  return displayName ?? shortId(workspaceId);
}

// 작업 종류별 GPU-hours 한 줄. 예: "train 4,848.2 · eval 1,078.3 GPU-hours". 종류 이름은 서버 값을
// 그대로 쓴다. 닫힌 어휘가 아니기 때문이다. 사용이 없으면 null 이다. 호출부가 줄을 생략한다.
export function formatUsageByType(shares: readonly TrainingGpuUsageShare[]): string | null {
  if (shares.length === 0) {
    return null;
  }
  const parts = shares.map((share) => `${share.key} ${formatGpuHours(share.gpu_hours)}`);
  return `${parts.join(" · ")} GPU-hours`;
}

// 지금 적용 중인 한도. 스케줄 때문에 유효 한도가 기준값과 다르면 둘을 함께 표시한다. 그 차이가
// 대개 "왜 지금 이만큼만 쓸 수 있는지"의 답이다. 한도가 없는 것(null)은 미설정이 아니라
// **무제한**이다. 0(아무것도 쓸 수 없음)과 다르다. 사람 행에서는 "선점한 workspace 중 하나
// 이상이 무제한" 이라는 뜻이다. 한도 행이 없는 경우에는 호출하지 않는다. 그 경우는 무제한이 아니다.
export function formatGpuLimit(effectiveGpuLimit: number | null, gpuLimit: number | null): string {
  const effective = effectiveGpuLimit ?? gpuLimit;
  if (effective === null) {
    return "무제한";
  }
  if (gpuLimit === null || gpuLimit === effective) {
    return formatNumber(effective);
  }
  return `${formatNumber(effective)} (기준 ${formatNumber(gpuLimit)})`;
}

// 한도 계열의 옵셔널 정수(group 상한 등). 미설정과 0 은 다르므로 null 만 "—" 로 표시한다.
export function formatOptionalGpuCount(count: number | null): string {
  return count === null ? emptyValue : formatNumber(count);
}

// days 인덱스. BE 계약은 월=0 … 일=6 이다(JS Date.getDay 의 일=0 과 다르다).
const weekdayNames = ["월", "화", "수", "목", "금", "토", "일"];
const weekdayCount = weekdayNames.length;

// 요일·시간대 스케줄 요약. 유효 한도가 기준 한도와 다른 이유는 대개 이 스케줄이다. 그래서 내역
// 행에서 한 줄로 표시한다. 시각은 행의 timezone 기준 wall-clock 이므로 **변환하지 않고 그대로**
// 쓴다. UTC 로 바꾸면 "09:00 부터" 가 다른 시각이 된다.
export function formatQuotaSchedule(
  schedule: readonly TrainingGpuQuotaScheduleEntry[],
  timezone: string | null,
): string {
  if (schedule.length === 0) {
    return "스케줄 없음";
  }
  const windows = schedule.map(formatScheduleWindow).join(" / ");
  return timezone === null ? windows : `${timezone} · ${windows}`;
}

function formatScheduleWindow(entry: TrainingGpuQuotaScheduleEntry): string {
  const limit = entry.gpu_limit === null ? "무제한" : `${formatNumber(entry.gpu_limit)}GPU`;
  const span = `${entry.start_time}–${entry.end_time}`;
  const days = formatScheduleDays(entry.days);
  return days === null ? `${span} ${limit}` : `${days} ${span} ${limit}`;
}

// 자주 쓰는 요일 집합은 이름으로 줄인다. "월·화·수·목·금" 보다 "평일" 이 빨리 읽힌다.
// 빈 배열은 요일 제약이 없다는 뜻이므로 라벨을 생략한다.
function formatScheduleDays(days: readonly number[]): string | null {
  const unique = [...new Set(days)].filter((day) => day >= 0 && day < weekdayCount).sort();
  if (unique.length === 0) {
    return null;
  }
  if (unique.length === weekdayCount) {
    return "매일";
  }
  const key = unique.join(",");
  if (key === "0,1,2,3,4") {
    return "평일";
  }
  if (key === "5,6") {
    return "주말";
  }
  return unique.map((day) => weekdayNames[day]).join("·");
}

const QUOTA_REQUEST_STATUS_LABELS: Record<TrainingGpuQuotaRequestStatus, string> = {
  pending: "대기",
  applied: "승인됨",
  rejected: "거절됨",
  cancelled: "철회됨",
};

export function quotaRequestStatusLabel(status: TrainingGpuQuotaRequestStatus): string {
  return QUOTA_REQUEST_STATUS_LABELS[status];
}

// 조회 실패 문구. c6 기본 문구가 영어이므로 소비처가 로케일 문구를 주입한다. 503 은 training-infra 가
// 잠시 응답하지 않는 경우이므로 다시 시도를 권한다. 502 는 upstream 이 응답했지만 거절한 경우로 구분한다.
export function resolveGpuQuotaErrorMessage(error: unknown): string {
  if (isHttpClientError(error)) {
    switch (error.code) {
      case "upstream.unavailable":
        return "GPU 정보를 제공하는 training-infra 에 연결하지 못했습니다. 잠시 후 새로고침해 주세요.";
      case "upstream.error":
        return "GPU 정보를 가져오지 못했습니다. 문제가 이어지면 training-infra 상태를 확인해 주세요.";
    }
  }
  return "GPU Quota 를 불러오지 못했습니다.";
}
